/**
 * Pricing constants and helpers for the two-tier subscription model.
 *
 * Standard:  £4.99 / month, £49.99 / year
 * Founding:  £3.00 / month, £30.00 / year (locked-in for life, capped at 250 subscribers)
 */

export type PlanTier = "standard" | "founding";
export type BillingInterval = "monthly" | "yearly";

export type PriceId =
  | "standard_monthly"
  | "standard_yearly"
  | "founding_monthly"
  | "founding_yearly";

export const FOUNDING_CAP = 250;

export const PRICES: Record<PriceId, { amount: number; label: string; subLabel: string }> = {
  standard_monthly: { amount: 4.99, label: "£4.99", subLabel: "/month" },
  standard_yearly: { amount: 49.99, label: "£49.99", subLabel: "/year" },
  founding_monthly: { amount: 3.0, label: "£3", subLabel: "/month" },
  founding_yearly: { amount: 30.0, label: "£30", subLabel: "/year" },
};

export const STANDARD_ANCHOR: Record<BillingInterval, string> = {
  monthly: "£4.99/month",
  yearly: "£49.99/year",
};

export function resolvePriceId(tier: PlanTier, interval: BillingInterval): PriceId {
  return `${tier}_${interval}` as PriceId;
}

export function yearlySavingsLabel(): string {
  // Founding yearly = £30 vs £36 → 2 months free (~17% off)
  // Standard yearly = £49.99 vs £59.88 → ~17% off
  return "2 months free";
}

/**
 * Single source of truth for paywall copy. Every dialog/sheet/page that
 * promotes the subscription reads from here so price or naming changes
 * never leave stale strings behind.
 *
 * Keep this neutral with respect to specific prices — the live price cards
 * inside `UpsellContent` render the actual numbers from `PRICES` and adapt
 * to whether Founding spots remain. These strings only describe the
 * product and the trial.
 */
export const PAYWALL_COPY = {
  /** Short product name. */
  productName: "CleanPlate Premium",
  /** CTA used on the small "upgrade" button inside SaveDialog and similar. */
  inlineCta: "Start your 14-day free trial",
  /** Reassurance line shown beneath the CTA in lightweight surfaces. */
  inlineSubtext: "Unlimited recipes, menus and shopping lists. Cancel anytime.",
  /** Title for the desktop UpgradeDialog header. */
  dialogTitle: "Unlock CleanPlate Premium",
  /** Description for the desktop UpgradeDialog header. */
  dialogDescription:
    "Start your 14-day free trial. Cancel anytime — no payment today. Your in-progress recipe will be saved automatically once checkout completes.",
  /** Headline for the /upgrade page (default variant). */
  pageHeadline: "Your complete recipe and meal-planning system",
  /** Subhead for the /upgrade page (default variant). */
  pageSubhead:
    "Turn your saved recipes into organised meals and ready-to-shop lists in minutes.",
  /** Headline shown on /upgrade when the user's free trial has expired. */
  expiredTrialHeadline: "Your trial's ended — keep your kitchen organised",
  /** Subhead shown on /upgrade when the user's free trial has expired. */
  expiredTrialSubhead:
    "All your recipes are still here. Upgrade to keep building menus and shopping lists.",
  /** Title for the SaveDialog when the free 5-recipe limit has been hit. */
  saveLimitTitle: "You've used all 5 free saves",
  /** Description for the SaveDialog limit-reached state. */
  saveLimitDescription:
    "Don't worry — your recipe is safe. Free up a slot or upgrade to keep it.",
} as const;
